/**
 * NutritionWorkspacePage — "מרכז הליווי", the nutrition coach's daily room.
 *
 * Route: /dashboard/nutrition            → client picker
 *        /dashboard/nutrition/:patientId  → one client pinned at the top, the
 *                                          four coaching tools underneath
 *
 * The four tabs (תפריטים · דוחות אכילה · מדידות · הערות) render HERE and only
 * here — the patient profile links in, it no longer hosts them. Deep links use
 * the profile's `?tab=` idiom so notifications, the pending queue and the
 * notes feed keep working unchanged.
 *
 * Nutrition clinics only: the page renders nothing for any other clinic type.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  ClipboardText,
  ForkKnife,
  MagnifyingGlass,
  NotePencil,
  Ruler,
  Target,
  UserCircle,
  Warning,
  X,
} from "@phosphor-icons/react";

import {
  selectClinicId,
  selectTherapistId,
  selectIsNutritionClinic,
} from "../../../store/slices/authSlice";
import {
  useGetPatientQuery,
  useGetPatientsQuery,
} from "../../../store/services/patientsApi";
import { useGetMenusForPatientQuery } from "../../../store/services/menusApi";
import { useGetMeasurementsQuery } from "../../../store/services/measurementsApi";
import { useGetDailyReportsQuery } from "../../../store/services/dailyReportsApi";
import { hasNutritionGoals } from "../../../firebase";
import NutritionGoalsModal from "../../../components/patients/nutrition/NutritionGoalsModal";
import PersonalPageLink from "../../../components/patients/nutrition/PersonalPageLink";
import { PageHeader } from "../../../components/shared";
import EmptyDraw from "../../../components/ui/EmptyDraw";
import MenusTab from "../../../components/patients/nutrition/MenusTab";
import MeasurementsTab from "../../../components/patients/nutrition/MeasurementsTab";
import DailyReportsTab, {
  REPORTS_LIMIT,
} from "../../../components/patients/nutrition/DailyReportsTab";
import NutritionNotesFeed from "../../../components/patients/nutrition/NutritionNotesFeed";
import { formatHebrewDate } from "../../../utils/dateUtils";
import "./NutritionWorkspacePage.css";
import ContentLoader from "../../../components/shared/ContentLoader";

// ============================================================================
// Constants
// ============================================================================

/** Tab order = the coach's day: menus first, then what waits on them. */
const TABS = [
  { id: "menus", label: "תפריטים", icon: ForkKnife },
  { id: "dailyReports", label: "דוחות אכילה", icon: ClipboardText },
  { id: "measurements", label: "מדידות", icon: Ruler },
  { id: "nutritionNotes", label: "הערות", icon: NotePencil },
];

const DEFAULT_TAB = "menus";

const TAB_IDS = new Set(TABS.map((tab) => tab.id));

const STATUS_CONFIG = {
  active: { label: "פעיל", color: "green" },
  waiting: { label: "בהמתנה", color: "yellow" },
  finished: { label: "סיים ליווי", color: "gray" },
  initial: { label: "פנייה ראשונית", color: "blue" },
};

/** Matches the patients list: fetch everything once, filter in memory. */
const PICKER_FETCH_LIMIT = 1000;

const initialsOf = (patient) =>
  `${patient?.firstName?.charAt(0) || ""}${patient?.lastName?.charAt(0) || ""}`;

const fullNameOf = (patient) =>
  `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim();

// ============================================================================
// Client picker (index route)
// ============================================================================

function ClientPicker({ clinicId, therapistId, onPick }) {
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useGetPatientsQuery(
    {
      clinicId,
      therapistId,
      status: "all",
      pageSize: PICKER_FETCH_LIMIT,
    },
    { skip: !clinicId || !therapistId }
  );

  useEffect(() => {
    if (!error) return;
    console.error("[CK-10300] Error loading clients for the nutrition workspace:", {
      errorCode: "CK-10300",
      errorMessage: error?.message || "unknown",
      clinicId,
      timestamp: new Date().toISOString(),
    });
  }, [error, clinicId]);

  const patients = useMemo(() => {
    const list = data?.patients || [];
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter((patient) =>
      fullNameOf(patient).toLowerCase().includes(term)
    );
  }, [data, search]);

  return (
    <div className="nwk-card">
      <div className="nwk-picker-toolbar">
        <div className="nwk-search-box">
          <MagnifyingGlass size={16} weight="bold" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="חיפוש לקוח/ה לפי שם..."
            aria-label="חיפוש לקוח/ה"
          />
          {search && (
            <button
              type="button"
              className="nwk-search-clear"
              onClick={() => setSearch("")}
              aria-label="ניקוי חיפוש"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
        <span className="nwk-picker-count">
          {patients.length} לקוחות
        </span>
      </div>

      {isLoading ? (
        <ContentLoader fill message="טוען לקוחות..." />
      ) : error ? (
        <div className="nwk-empty">
          <EmptyDraw variant="search" />
          <p>טעינת רשימת הלקוחות נכשלה</p>
        </div>
      ) : patients.length === 0 ? (
        <div className="nwk-empty">
          <EmptyDraw variant={search ? "search" : "patients"} />
          <p>
            {search
              ? "לא נמצאו לקוחות התואמים לחיפוש"
              : "עדיין אין לקוחות — הוסיפו לקוח/ה ראשון/ה מרשימת הלקוחות"}
          </p>
        </div>
      ) : (
        <ul className="nwk-picker-list">
          {patients.map((patient) => {
            const status = STATUS_CONFIG[patient.status] || STATUS_CONFIG.active;
            const isInactive = patient.isActive === false;
            return (
              <li key={patient.id}>
                <button
                  type="button"
                  className={`nwk-picker-row ${isInactive ? "inactive" : ""}`}
                  onClick={() => onPick(patient.id)}
                >
                  <span className="nwk-avatar sm">{initialsOf(patient)}</span>
                  <span className="nwk-picker-name">{fullNameOf(patient)}</span>
                  <span
                    className={`nwk-status-badge status-${isInactive ? "gray" : status.color}`}
                  >
                    {isInactive ? "לא פעיל" : status.label}
                  </span>
                  <ArrowRight size={14} weight="bold" className="nwk-picker-arrow" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Client header (sticky)
// ============================================================================

function ClientHeader({ clinicId, patientId, patient }) {
  // The three key numbers reuse the exact query args the tabs use, so RTK
  // serves them from the same cache entries — no extra reads.
  const { data: menus = [] } = useGetMenusForPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );
  const { data: measurements = [] } = useGetMeasurementsQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );
  const { data: reports = [] } = useGetDailyReportsQuery(
    { clinicId, patientId, limitCount: REPORTS_LIMIT },
    { skip: !clinicId || !patientId }
  );

  const activeMenuId = patient?.nutrition?.activeMenuId || null;
  const activeMenu = useMemo(
    () => (activeMenuId ? menus.find((menu) => menu.id === activeMenuId) : null),
    [menus, activeMenuId]
  );

  const lastMeasurement = useMemo(() => {
    if (!measurements.length) return null;
    return measurements.reduce((latest, item) => {
      const a = new Date(item.date || item.createdAt || 0).getTime();
      const b = new Date(latest.date || latest.createdAt || 0).getTime();
      return a > b ? item : latest;
    }, measurements[0]);
  }, [measurements]);

  const lastReport = useMemo(() => {
    if (!reports.length) return null;
    return reports.reduce((latest, item) => {
      const a = String(item.date || item.id || "");
      const b = String(latest.date || latest.id || "");
      return a > b ? item : latest;
    }, reports[0]);
  }, [reports]);

  const isInactive = patient?.isActive === false;
  const status = STATUS_CONFIG[patient?.status] || STATUS_CONFIG.active;

  return (
    <header className="nwk-client-header">
      <div className="nwk-client-main">
        <span className="nwk-avatar">{initialsOf(patient)}</span>
        <div className="nwk-client-identity">
          <h2 className="nwk-client-name">{fullNameOf(patient)}</h2>
          <div className="nwk-client-badges">
            <span
              className={`nwk-status-badge status-${isInactive ? "gray" : status.color}`}
            >
              {isInactive ? "לא פעיל" : status.label}
            </span>
            {patient?.phone && (
              <span className="nwk-client-phone" dir="ltr">
                {patient.phone}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="nwk-client-link">
        <PersonalPageLink clinicId={clinicId} patient={patient} variant="row" />
      </div>

      <dl className="nwk-key-numbers">
        <div className="nwk-key">
          <dt>תפריט פעיל</dt>
          <dd className={activeMenu ? "" : "muted"}>
            {activeMenu ? activeMenu.name || "תפריט ללא שם" : "אין תפריט פעיל"}
          </dd>
        </div>
        <div className="nwk-key">
          <dt>מדידה אחרונה</dt>
          <dd className={lastMeasurement ? "" : "muted"}>
            {lastMeasurement
              ? formatHebrewDate(lastMeasurement.date || lastMeasurement.createdAt)
              : "טרם נמדד/ה"}
          </dd>
        </div>
        <div className="nwk-key">
          <dt>דוח אכילה אחרון</dt>
          <dd className={lastReport ? "" : "muted"}>
            {lastReport
              ? formatHebrewDate(lastReport.date || lastReport.id)
              : "עדיין אין דוחות"}
          </dd>
        </div>
      </dl>
    </header>
  );
}

// ============================================================================
// Page
// ============================================================================

const NutritionWorkspacePage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const isNutritionClinic = useSelector(selectIsNutritionClinic);

  const requestedTab = searchParams.get("tab");
  const activeTab = TAB_IDS.has(requestedTab) ? requestedTab : DEFAULT_TAB;

  // "לא הוגדרו יעדים" — the one-click set action. `?setGoals=1` (from the
  // pending queue) opens it on arrival; the param is consumed so a refresh
  // does not reopen it.
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  useEffect(() => {
    if (searchParams.get("setGoals") !== "1") return;
    setShowGoalsModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete("setGoals");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setActiveTab = useCallback(
    (tabId) => {
      if (!TAB_IDS.has(tabId)) return;
      const next = new URLSearchParams(searchParams);
      next.set("tab", tabId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const {
    data: patient,
    isLoading: isLoadingPatient,
    error: patientError,
  } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );

  useEffect(() => {
    if (!patientError) return;
    console.error("[CK-10303] Error loading the client for the nutrition workspace:", {
      errorCode: "CK-10303",
      errorMessage: patientError?.message || "unknown",
      clinicId,
      patientId,
      timestamp: new Date().toISOString(),
    });
  }, [patientError, clinicId, patientId]);

  // Defensive gate — the sidebar entry and the profile link are already hidden.
  if (!isNutritionClinic) return null;

  // ----- index: pick a client -----
  if (!patientId) {
    return (
      <div className="nwk-page">
        <PageHeader
          icon={UserCircle}
          title="מרכז הליווי"
          subtitle="בחרו לקוח/ה כדי לפתוח את התפריטים, דוחות האכילה, המדידות וההערות שלו/ה"
        />
        <ClientPicker
          clinicId={clinicId}
          // Same source as the patients page: always pass therapistId.
          // Passing undefined for admins tripped the `skip: !therapistId`
          // gate and the picker rendered "no clients" forever (Round 3 / Phase 22).
          therapistId={therapistId}
          onPick={(id) => navigate(`/dashboard/nutrition/${id}`)}
        />
      </div>
    );
  }

  // ----- one client -----
  const renderTab = () => {
    switch (activeTab) {
      case "dailyReports":
        return <DailyReportsTab clinicId={clinicId} patientId={patientId} patient={patient} />;
      case "measurements":
        return (
          <MeasurementsTab
            clinicId={clinicId}
            patientId={patientId}
            patient={patient}
            // BMR/RMR need gender + birth date — they live on the profile's
            // first tab; open it straight in edit mode
            onMissingBodyData={() =>
              navigate(`/dashboard/patients/${patientId}?edit=true`)
            }
          />
        );
      case "nutritionNotes":
        return (
          <NutritionNotesFeed
            clinicId={clinicId}
            patientId={patientId}
            onOpenTab={setActiveTab}
          />
        );
      case "menus":
      default:
        return <MenusTab clinicId={clinicId} patientId={patientId} patient={patient} />;
    }
  };

  return (
    <div className="nwk-page">
      <div className="nwk-topbar">
        <button
          type="button"
          className="nwk-back-btn"
          onClick={() => navigate("/dashboard/nutrition")}
        >
          <ArrowRight size={16} weight="bold" />
          <span>לרשימת הלקוחות</span>
        </button>
        {patient && (
          <button
            type="button"
            className="nwk-link-btn"
            onClick={() => navigate(`/dashboard/patients/${patientId}`)}
          >
            <UserCircle size={15} weight="bold" />
            <span>לכרטיס הלקוח/ה</span>
          </button>
        )}
      </div>

      {isLoadingPatient ? (
        <div className="nwk-card">
          <ContentLoader message="טוען את פרטי הלקוח/ה..." />
        </div>
      ) : !patient ? (
        <div className="nwk-card">
          <div className="nwk-empty">
            <EmptyDraw variant="search" />
            <p>הלקוח/ה לא נמצא/ה</p>
            <button
              type="button"
              className="nwk-ghost-btn"
              onClick={() => navigate("/dashboard/nutrition")}
            >
              חזרה לרשימת הלקוחות
            </button>
          </div>
        </div>
      ) : (
        <>
          <ClientHeader clinicId={clinicId} patientId={patientId} patient={patient} />

          {/* Persistent until goals exist — skipping them at intake is allowed,
              never forgotten — unless the coach dismissed the reminder for
              this client from the pending queue (Round 3 / Phase 23) */}
          {!hasNutritionGoals(patient.nutrition?.goals) &&
            !patient.nutritionGoalsPromptDismissedAt && (
            <div className="nwk-banner" role="status">
              <Warning size={18} weight="fill" />
              <span className="nwk-banner-text">
                <strong>לא הוגדרו יעדים יומיים ללקוח זה.</strong> בלי יעדים, הלקוח/ה
                רואה/ה סיכומים בלי מטרה והדוחות לא מסומנים מול טווח.
              </span>
              <button
                type="button"
                className="nwk-banner-btn"
                onClick={() => setShowGoalsModal(true)}
              >
                <Target size={15} weight="bold" />
                הגדרת יעדים
              </button>
            </div>
          )}

          {showGoalsModal && (
            <NutritionGoalsModal
              clinicId={clinicId}
              patient={patient}
              onClose={() => setShowGoalsModal(false)}
            />
          )}

          <div className="nwk-card nwk-board">
            <div className="nwk-tabs" role="tablist" aria-label="כלי הליווי">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`nwk-tab ${isActive ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={15} weight={isActive ? "fill" : "regular"} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="nwk-board-body">{renderTab()}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default NutritionWorkspacePage;
